import type {
    ContentTree,
    SiteContentElement,
    SiteContentElementKind,
    ExternalLinkElement,
    InternalLinkElement,
    LinkCollectionElement,
    ImageLinkCollectionElement,
    ImageElement,
    PlainTextElement,
    PlainMultilineTextElement,
    RichTextElement,
    TableElement,
    PartitionedTableElement,
    HighlightsCollectionElement,
    GovernanceRoleElement,
    ProductApplicationSegmentCardCollectionElement,
    BrandCardCollectionElement,
    CompanyFocusTopicElement,
    CompanyFocusTopicContentSectionEntry,
    CompanyFocusTopicExternalLinkEntry,
    CompanyFocusTopicInternalLinkEntry,
    InfoBannerCollectionEntry,
    InfoBannerCollectionElement,
    ImageLinkCollectionEntry,
} from '../types/SiteContentElement';

// Utility to detect a leaf SiteContentElement
function isLeaf(node: any): node is SiteContentElement {
    return node && typeof node.kind === 'string';
}

export const DEBUG_LEVELS = ['none', 'prefix', 'shortPath', 'fullPath'] as const;
export type DebugLevel = (typeof DEBUG_LEVELS)[number];

/**
 * Level 1 (prefix): prepend `PIM:` to all textual leaves (plain, multiline, rich) and companyFocusTopic entries.
 * URLs (linkRoute, linkUrl, imageUrl) are left untouched.
 */
export function prefixTreeTexts(tree: ContentTree): ContentTree {
    const result: ContentTree = {};
    for (const key of Object.keys(tree)) {
        const node = tree[key];

        if (Array.isArray(node)) {
            result[key] = node.map((el) => {
                switch (el.kind) {
                    case 'plainText':
                    case 'plainMultilineText':
                        return { ...el, content: `PIM:${el.content}` };
                    case 'richText':
                        return {
                            ...el,
                            content: `PIM:${el.content}`,
                            sanitizedContent: `PIM:${el.sanitizedContent}`,
                        };
                    case 'internalLink':
                    case 'externalLink':
                        return { ...el, linkText: `PIM:${el.linkText}` };
                    case 'linkCollection':
                        return {
                            ...el,
                            links: el.links.map((link) => ({
                                ...link,
                                linkText: `PIM:${link.linkText}`,
                            })),
                        };
                    case 'imageLinkCollection':
                        return {
                            ...el,
                            links: el.links.map(
                                (link): ImageLinkCollectionEntry => ({
                                    ...link,
                                    tooltip: `PIM:${link.tooltip}`,
                                    imageAltText: `PIM:${link.imageAltText}`,
                                })
                            ),
                        } satisfies ImageLinkCollectionElement;
                    case 'highlightsCollection':
                        return {
                            ...el,
                            entries: el.entries.map((h) => ({
                                ...h,
                                title: `PIM:${h.title}`,
                                text: `PIM:${h.text}`,
                            })),
                        };
                    case 'governanceRole':
                        return {
                            ...el,
                            title: `PIM:${el.title}`,
                            personProfiles: el.personProfiles.map((pp) => ({
                                name: `PIM:${pp.name}`,
                                content: `PIM:${pp.content}`,
                            })),
                        };
                    case 'productApplicationSegmentCardCollection':
                        return {
                            ...el,
                            entries: el.entries.map((card) => ({
                                ...card,
                                name: `PIM:${card.name}`,
                            })),
                        };
                    case 'brandCardCollection':
                        return {
                            ...el,
                            entries: el.entries.map((b) => ({
                                ...b,
                                name: `PIM:${b.name}`,
                                text: `PIM:${b.text}`,
                            })),
                        };
                    case 'companyFocusTopic':
                        return {
                            ...el,
                            title: `PIM:${el.title}`,
                            entries: el.entries.map((entry) => {
                                switch (entry.kind) {
                                    case 'contentSectionFieldCollection':
                                        return {
                                            ...entry,
                                            title: `PIM:${entry.title}`,
                                            content: `PIM:${entry.content}`,
                                            sanitizedContent: `PIM:${entry.sanitizedContent}`,
                                        } as CompanyFocusTopicContentSectionEntry;
                                    case 'internalLinkFieldCollection':
                                        return {
                                            ...entry,
                                            linkText: `PIM:${entry.linkText}`,
                                        } as CompanyFocusTopicInternalLinkEntry;
                                    case 'externalLinkFieldCollection':
                                        return {
                                            ...entry,
                                            linkText: `PIM:${entry.linkText}`,
                                        } as CompanyFocusTopicExternalLinkEntry;
                                    default:
                                        return entry;
                                }
                            }),
                        };
                    case 'infoBannerCollection':
                        return {
                            ...el,
                            entries: el.entries.map(
                                (banner) =>
                                    ({
                                        ...banner,
                                        content: `PIM:${banner.content}`,
                                        sanitizedContent: `PIM:${banner.sanitizedContent}`,
                                    }) as InfoBannerCollectionEntry
                            ),
                        };
                    default:
                        return el;
                }
            });
        } else if (isLeaf(node)) {
            switch (node.kind) {
                case 'plainText':
                case 'plainMultilineText':
                    result[key] = { ...node, content: `PIM:${node.content}` };
                    break;
                case 'richText':
                    result[key] = {
                        ...node,
                        content: `PIM:${node.content}`,
                        sanitizedContent: `PIM:${node.sanitizedContent}`,
                    };
                    break;
                case 'internalLink':
                case 'externalLink':
                    result[key] = { ...node, linkText: `PIM:${node.linkText}` };
                    break;
                case 'linkCollection':
                    result[key] = {
                        ...node,
                        links: node.links.map((link) => ({
                            ...link,
                            linkText: `PIM:${link.linkText}`,
                        })),
                    };
                    break;
                case 'imageLinkCollection':
                    result[key] = {
                        ...node,
                        links: node.links.map(
                            (link): ImageLinkCollectionEntry => ({
                                ...link,
                                tooltip: `PIM:${link.tooltip}`,
                                imageAltText: `PIM:${link.imageAltText}`,
                            })
                        ),
                    } satisfies ImageLinkCollectionElement;
                    break;
                case 'highlightsCollection':
                    result[key] = {
                        ...node,
                        entries: node.entries.map((h) => ({
                            ...h,
                            title: `PIM:${h.title}`,
                            text: `PIM:${h.text}`,
                        })),
                    };
                    break;
                case 'governanceRole':
                    result[key] = {
                        ...node,
                        title: `PIM:${node.title}`,
                        personProfiles: node.personProfiles.map((pp) => ({
                            name: `PIM:${pp.name}`,
                            content: `PIM:${pp.content}`,
                        })),
                    };
                    break;
                case 'productApplicationSegmentCardCollection':
                    result[key] = {
                        ...node,
                        entries: node.entries.map((card) => ({
                            ...card,
                            name: `PIM:${card.name}`,
                        })),
                    };
                    break;
                case 'brandCardCollection':
                    result[key] = {
                        ...node,
                        entries: node.entries.map((b) => ({
                            ...b,
                            name: `PIM:${b.name}`,
                            text: `PIM:${b.text}`,
                        })),
                    };
                    break;
                case 'companyFocusTopic':
                    result[key] = {
                        ...node,
                        title: `PIM:${node.title}`,
                        entries: node.entries.map((entry) => {
                            switch (entry.kind) {
                                case 'contentSectionFieldCollection':
                                    return {
                                        ...entry,
                                        title: `PIM:${entry.title}`,
                                        content: `PIM:${entry.content}`,
                                        sanitizedContent: `PIM:${entry.sanitizedContent}`,
                                    } as CompanyFocusTopicContentSectionEntry;
                                case 'internalLinkFieldCollection':
                                    return {
                                        ...entry,
                                        linkText: `PIM:${entry.linkText}`,
                                    } as CompanyFocusTopicInternalLinkEntry;
                                case 'externalLinkFieldCollection':
                                    return {
                                        ...entry,
                                        linkText: `PIM:${entry.linkText}`,
                                    } as CompanyFocusTopicExternalLinkEntry;
                                default:
                                    return entry;
                            }
                        }),
                    } as CompanyFocusTopicElement;
                    break;
                case 'infoBannerCollection':
                    result[key] = {
                        ...node,
                        entries: node.entries.map(
                            (banner) =>
                                ({
                                    ...banner,
                                    content: `PIM:${banner.content}`,
                                    sanitizedContent: `PIM:${banner.sanitizedContent}`,
                                }) as InfoBannerCollectionEntry
                        ),
                    };
                    break;
                default:
                    result[key] = node;
            }
        } else {
            // nested subtree
            result[key] = prefixTreeTexts(node as ContentTree);
        }
    }
    return result;
}

/**
 * Build the display path:
 *  - 'shortPath': e.g. A.C.mainTab.title
 *  - 'fullPath':  e.g. About.Careers.mainTab.title
 */
function makeDisplayPath(
    fullDotPath: string,
    level: Exclude<DebugLevel, 'none' | 'prefix'>
): string {
    if (level === 'fullPath') {
        return fullDotPath;
    }
    // level === 'shortPath'
    const parts = fullDotPath.split('.');
    if (parts.length < 2) return fullDotPath;
    const leafPair = parts.slice(-2).join('.');
    const folders = parts.slice(0, -2).map((p) => p.charAt(0).toUpperCase());
    return folders.join('.') + '.' + leafPair;
}

// Create placeholder based on kind, injecting the chosen displayPath
function createPlaceholderForKind(
    kind: SiteContentElementKind,
    displayPath: string
): SiteContentElement {
    switch (kind) {
        case 'plainText':
            return { kind, key: '', content: displayPath } as PlainTextElement;
        case 'plainMultilineText':
            return { kind, key: '', content: displayPath } as PlainMultilineTextElement;
        case 'richText':
            return {
                kind,
                key: '',
                content: displayPath,
                sanitizedContent: displayPath,
            } as RichTextElement;
        case 'image':
            return { kind, key: '', imageUrl: displayPath } as ImageElement;
        case 'internalLink':
            return {
                kind,
                key: '',
                linkText: displayPath,
                linkRoute: displayPath,
            } as InternalLinkElement;
        case 'externalLink':
            return {
                kind,
                key: '',
                linkText: displayPath,
                linkUrl: displayPath,
            } as ExternalLinkElement;
        case 'linkCollection':
            return {
                kind,
                key: '',
                links: [
                    {
                        kind: 'internalLinkFieldCollection',
                        linkText: displayPath,
                        linkRoute: displayPath,
                    },
                ],
            } as LinkCollectionElement;
        case 'imageLinkCollection':
            return {
                kind,
                key: '',
                links: [
                    {
                        kind: 'internalImageLinkFieldCollection',
                        tooltip: displayPath,
                        linkRoute: displayPath,
                        imageUrl: displayPath,
                    },
                ],
            } as ImageLinkCollectionElement;
        case 'table':
            return {
                kind,
                key: '',
                tableHeader: [displayPath],
                tableBody: [[displayPath]],
            } as TableElement;
        case 'partitionedTable':
            return {
                kind,
                key: '',
                commonTableHeader: [displayPath],
                tablePartitions: [
                    {
                        selectionKey: displayPath,
                        tableHeader: [displayPath],
                        tableBody: [[displayPath]],
                    },
                ],
            } as PartitionedTableElement;
        case 'highlightsCollection':
            return {
                kind,
                key: '',
                entries: [{ title: displayPath, imageUrl: displayPath, text: displayPath }],
            } as HighlightsCollectionElement;
        case 'governanceRole':
            return {
                kind,
                key: '',
                title: displayPath,
                personProfiles: [{ name: displayPath, content: displayPath }],
            } as GovernanceRoleElement;
        case 'productApplicationSegmentCardCollection':
            return {
                kind,
                key: '',
                entries: [{ imageUrl: displayPath, name: displayPath, route: displayPath }],
            } as ProductApplicationSegmentCardCollectionElement;
        case 'brandCardCollection':
            return {
                kind,
                key: '',
                entries: [
                    {
                        brandId: displayPath,
                        name: displayPath,
                        logoUrl: displayPath,
                        text: displayPath,
                        externalLinkUrl: displayPath,
                    },
                ],
            } as BrandCardCollectionElement;
        case 'companyFocusTopic':
            return {
                kind,
                key: '',
                title: displayPath,
                entries: [
                    {
                        kind: 'contentSectionFieldCollection',
                        title: displayPath,
                        content: displayPath,
                    },
                ],
            } as CompanyFocusTopicElement;
        case 'infoBannerCollection':
            return {
                kind,
                key: '',
                entries: [
                    {
                        identifier: displayPath,
                        content: displayPath,
                        sanitizedContent: displayPath,
                    } as InfoBannerCollectionEntry,
                ],
            } as InfoBannerCollectionElement;
        default:
            return { kind: 'plainText', key: '', content: displayPath } as PlainTextElement;
    }
}

/**
 * Recursively walk and replace with either short or full paths, based on symbolic level.
 */
export function mapTreeToPaths(
    tree: ContentTree,
    baseDotPath: string = '',
    level: DebugLevel
): ContentTree {
    const result: ContentTree = {};
    for (const key of Object.keys(tree)) {
        const node = tree[key];
        const fullDot = baseDotPath ? `${baseDotPath}.${key}` : key;
        // if symbolic level is not a path-overlay, treat as no-op
        if (level === 'none') {
            result[key] = node as any;
            continue;
        }
        // build display path for overlay
        const displayPath = makeDisplayPath(
            fullDot,
            level === 'shortPath' ? 'shortPath' : 'fullPath'
        );

        if (Array.isArray(node)) {
            const firstKind = isLeaf(node[0]) ? node[0].kind : undefined;
            const placeholder = firstKind
                ? createPlaceholderForKind(firstKind, displayPath)
                : ({ kind: 'plainText', key: '', content: displayPath } as PlainTextElement);
            result[key] = [placeholder];
        } else if (isLeaf(node)) {
            result[key] = createPlaceholderForKind(node.kind, displayPath);
        } else {
            result[key] = mapTreeToPaths(node as ContentTree, fullDot, level);
        }
    }
    return result;
}
